/**
 * @file features-slice.ts
 * @description Store slice for feature flags and triathlon-specific state.
 *
 * This slice manages:
 * - Sport enablement flags (running, cycling, swimming, triathlon)
 * - Triathlon-specific progression settings
 *
 * Feature flags allow staged rollout of new functionality without
 * affecting existing running and cycling modes.
 */

import type { StateCreator } from 'zustand';
import type { AppState } from '../types';
import type { EnabledSports, TriathlonProgression } from '@/types';
import { cloneTriathlonProgression, defaultEnabledSports } from '../constants';
import { updateActiveProfile } from '../helpers';
import { mergeTriathlonProgression } from '@/lib/triathlon/weekly-rhythm';

type FeaturesSlice = Pick<
    AppState,
    'enabledSports' | 'triathlonProgression' | 'setEnabledSports' | 'setTriathlonProgression' | 'enforcePremium' | 'setEnforcePremium' | 'paidFeatures' | 'setPaidFeatures'
>;

export const createFeaturesSlice: StateCreator<AppState, [], [], FeaturesSlice> = (set) => ({
    // =========================================================================
    // STATE
    // =========================================================================

    enabledSports: { ...defaultEnabledSports },
    triathlonProgression: cloneTriathlonProgression(),

    // =========================================================================
    // ACTIONS
    // =========================================================================

    enforcePremium: false,

    setEnforcePremium: (enabled: boolean) => set({ enforcePremium: enabled }),

    paidFeatures: {
        intervalsSync: false,
        workoutUpload: false,
        planPrinting: false,
    },

    setPaidFeatures: (features: Partial<AppState['paidFeatures']>) => set((state) => ({
        paidFeatures: { ...state.paidFeatures, ...features }
    })),

    /**
     * Update enabled sports flags.
     * Allows partial updates (e.g., just enable triathlon).
     *
     * @example
     * setEnabledSports({ triathlon: true }); // Enable triathlon
     * setEnabledSports({ swimming: true, triathlon: true }); // Enable both
     */
    setEnabledSports: (sports: Partial<EnabledSports>) => {
        set((state) => ({
            enabledSports: {
                ...state.enabledSports,
                ...sports,
            },
        }));
    },

    /**
     * Update triathlon progression settings.
     * Only used when athlete.sport === 'Triathlon'.
     *
     * Handles nested updates for swimmingMetrics and disciplineDistribution.
     */
    setTriathlonProgression: (prog: Partial<TriathlonProgression>) => {
        set((state) => {
            const nextTriathlonProgression = mergeTriathlonProgression(state.triathlonProgression, prog);

            return {
                triathlonProgression: nextTriathlonProgression,
                ...(updateActiveProfile(state, { triathlonProgression: nextTriathlonProgression }) as Partial<AppState>),
            };
        });
    },
});
